import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchLatestFinancialRatios } from '../api';

export const RATIOS_CONCURRENCY = 4;
export const RATIOS_TIMEOUT_MS = 15000;

// Results live only for this mounted page. Changing the symbol set preserves
// completed rows, cancels pending work, and starts a new bounded queue.
export function useLatestFinancialRatios(symbols) {
  const symbolKey = JSON.stringify([...new Set(symbols)]);
  const completed = useRef({});
  const [results, setResults] = useState({});

  useEffect(() => {
    const requested = JSON.parse(symbolKey);
    let cancelled = false;
    const controllers = new Set();
    completed.current = Object.fromEntries(
      requested.filter(symbol => completed.current[symbol])
        .map(symbol => [symbol, completed.current[symbol]])
    );
    setResults({ ...completed.current });
    const pending = requested.filter(symbol => !completed.current[symbol]);
    let next = 0;

    const worker = async () => {
      while (!cancelled && next < pending.length) {
        const symbol = pending[next++];
        const controller = new AbortController();
        controllers.add(controller);
        const timer = setTimeout(() => controller.abort(), RATIOS_TIMEOUT_MS);
        let result;
        try {
          result = await fetchLatestFinancialRatios(symbol, { signal: controller.signal });
        } catch (error) {
          result = { status: 'error', reason: controller.signal.aborted ? 'timeout' : 'fetch_failed', data: null };
        } finally {
          clearTimeout(timer);
          controllers.delete(controller);
        }
        if (cancelled) return;
        completed.current[symbol] = result;
        setResults(previous => ({ ...previous, [symbol]: result }));
      }
    };
    for (let i = 0; i < Math.min(RATIOS_CONCURRENCY, pending.length); i += 1) worker();

    return () => {
      cancelled = true;
      controllers.forEach(controller => controller.abort());
    };
  }, [symbolKey]);

  return useMemo(() => {
    const requested = JSON.parse(symbolKey);
    return {
      results: Object.fromEntries(requested.map(symbol => [symbol, results[symbol] || { status: 'loading', data: null }])),
      metricsData: Object.fromEntries(requested.filter(symbol => results[symbol]?.status === 'ready')
        .map(symbol => [symbol, results[symbol].data]))
    };
  }, [results, symbolKey]);
}
